import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TaskForm from '../components/TaskForm';
import Task from '../utils/TaskMaster';
import { v4 as uuidv4 } from 'uuid';
import { 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Stack,
  FormControl,
  FormControlLabel,
  Switch,
  Alert,
  Slider,
  Typography,
  Box,
  Tooltip
} from '@mui/material';
import { AddBox, AutoFixHigh, ContentCopy } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import TaskInputForm from '../components/taskInputForm';
import TaskSelectionDialog from '../components/TaskSelectionDialog';
import { EqBoosterSlider, LoadingIndicator } from '../components/assets';
import PriorityPanel from '../components/PriorityPanel';
import CategoryPicker from '../components/CategoryPicker';
import SubscriptionDialog from '../components/SubscriptionDialog';
import {
  getTaskTree,
  createTaskTree,
  replaceTaskTree,
  deleteTaskTree,
  generateTask,
  fetchAllTasks,
} from '../utils/api';

const TaskManager = () => {
  const { taskId } = useParams(); // Get the taskId from the URL if it exists
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(!!taskId); // Loading if we're editing a task
  const [error, setError] = useState(null);
  const [pendingChanges, setPendingChanges] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [quotaDetails, setQuotaDetails] = useState(null);
  const [aiOperationKey, setAiOperationKey] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [aiForm, setAiForm] = useState({
    prompt: '',
    category: '',
    category_id: null,
    importance: 1,
    frequency: false,
    timeEstimation: false,
    dueDate: '',
    breakdowns: 3,
    difficulty: 5,
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDeletingTask, setIsDeletingTask] = useState(false);
  const[manualTask, setManualTask] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);
  const [currentTasks, setCurrentTasks] = useState([]);
  const [isLoadingDuplicateTasks, setIsLoadingDuplicateTasks] = useState(false);
  const handleClose = () => setManualTask(null);
  const navigate = useNavigate();

  // Determine if we're editing or creating based on taskId presence
  const isEditing = !!taskId;

  const createBlankTask = () => new Task({
    id: uuidv4(),
    creation_date: new Date(),
    title: '',
    parent_id: null,
    category: '',
    category_id: null,
    complete: null,
    frequency: 0,
    working_time: 0,
    importance: 1,
    due_date: null,
    duration: 0,
    alarm: false,
    ordered: false,
    subtasks: []
  });

  const handleCreateManualTask = () => {
    const newTask = createBlankTask();
    setTask(newTask);
    setManualTask(newTask);
  };

  useEffect(() => {
    // If taskId is provided, fetch the task data
    if (taskId) {
      setLoading(true);
      getTaskTree(taskId)
        .then(data => {
          const fetchedTask = new Task(data);
          setTask(fetchedTask);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error fetching task data:', error);
          setError('Failed to load task. Please try again.');
          setLoading(false);
        });
    } 
  }, [taskId]);

  const handleTaskSubmit = async (taskData) => {
    console.log('Task Data:', taskData);
    
    try {
      const jsonData = taskData.toFlatWritePayload();
      console.log('JSON Task Data:', jsonData);

      // Determine if we're creating or updating based on the presence of taskId
      let data;
      if (isEditing) {
        data = await replaceTaskTree(taskId, jsonData);
      } else {
        data = await createTaskTree(jsonData);
      }
      if (data?.task) {
        setTask(new Task(data.task));
      }
      console.log(`Task ${isEditing ? 'updated' : 'created'} successfully`, data);
      setPendingChanges(false);
      navigate('/user-home');
    } catch (error) {
      console.error('Error:', error);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  const handleDeleteTask = async () => {
    if (!taskId || isDeletingTask) return;
    
    try {
      const confirmDelete = window.confirm('Are you sure you want to delete this task? This action cannot be undone.');
      
      if (!confirmDelete) {
        return; // User cancelled the deletion
      }

      setIsDeletingTask(true);
      await deleteTaskTree(taskId);
      console.log('Task deleted successfully');
      navigate('/user-home');
    } catch (error) {
      console.error('Error deleting task:', error);
      setError('An unexpected error occurred while deleting. Please try again.');
    } finally {
      setIsDeletingTask(false);
    }
  };

  const handleRootDelete = async () => {
    if (isEditing) {
      await handleDeleteTask();
      return;
    }

    const confirmDelete = window.confirm('Discard this task tree? This action cannot be undone.');
    if (!confirmDelete) return;

    setTask(null);
    setManualTask(null);
    setPendingChanges(false);
  };

  const handleTaskUpdate = (updatedTask) => {
    console.log('Task updated:', updatedTask);
    setTask(new Task(updatedTask));
  }
  const handleSubtaskDelete = (parentId, subtaskId, updatedTask) => {
    // Update the task state to reflect the deletion in the UI
    setTask(updatedTask);
    
    // Mark that there are pending changes that need to be saved
    setPendingChanges(true);
    
    // We don't make an immediate API call here - the changes will be sent
    // when the user submits the form
    console.log(`Subtask ${subtaskId} marked for deletion from parent ${parentId}`);
  };

  // Notify user about unsaved changes when they try to leave
  useEffect(() => {
    if (pendingChanges) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [pendingChanges]);

  const handleAISubmit = async (e) => {
    e.preventDefault();
    setIsGenerating(true);
    setAiError(null);

    const formData = {
      prompt: aiForm.prompt,
      category: aiForm.category_id ? aiForm.category : '',
      importance: parseInt(aiForm.importance, 10),
      frequency: aiForm.frequency,
      timeEstimation: aiForm.timeEstimation,
      dueDate: aiForm.dueDate || null,
      breakdowns: parseInt(aiForm.breakdowns, 10),
      overwhelm: parseInt(aiForm.difficulty, 10)
    };

    const operationKey = aiOperationKey || uuidv4();
    setAiOperationKey(operationKey);

    try {
      console.log('AI Generation Prompt:', formData);
      const data = await generateTask(formData, operationKey);
      const generatedTask = new Task({ ...data });
      const applySelectedCategory = (node) => {
        node.category_id = aiForm.category_id;
        node.category = aiForm.category || 'no-category';
        node.subtasks.forEach(applySelectedCategory);
      };
      applySelectedCategory(generatedTask);
      generatedTask.updateAllSubtasksOrder();

      setTask(generatedTask);
      setShowAIModal(false);
      setPendingChanges(false);
      setAiOperationKey(null);
      window.dispatchEvent(new Event('billingStatusUpdate'));
    } catch (error) {
      console.error('Error generating task:', error);
      const preservesOperation = (
        error?.data?.code === 'ai_generation_in_progress'
        || !Number.isInteger(error?.status)
      );
      if (!preservesOperation) {
        setAiOperationKey(null);
      }
      if (error?.status === 402 && error?.data?.code === 'ai_quota_exhausted') {
        setQuotaDetails(error.data);
        setShowAIModal(false);
        setShowSubscriptionDialog(true);
      } else {
        setAiError(error?.data?.message || 'An error occurred while generating the task');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAiFieldChange = (field, value) => {
    setAiForm((prev) => ({ ...prev, [field]: value }));
  };

  const returnToAIForm = () => {
    setShowSubscriptionDialog(false);
    setQuotaDetails(null);
    setAiError(null);
    setShowAIModal(true);
  };

  const openDuplicateModal = async () => {
    setDuplicateError(null);
    setShowDuplicateModal(true);
    setIsLoadingDuplicateTasks(true);

    try {
      const data = await fetchAllTasks();
      setCurrentTasks(data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setDuplicateError('Failed to load available tasks.');
    } finally {
      setIsLoadingDuplicateTasks(false);
    }
  };

  const closeDuplicateModal = () => {
    setShowDuplicateModal(false);
    setDuplicateError(null);
  };

  const duplicateSelectedTask = (selectedId) => {
    if (!selectedId) {
      setDuplicateError('Please select a task to duplicate');
      return;
    }

    const taskRoots = currentTasks.map((currentTask) => new Task(currentTask));
    let taskToDuplicate = null;
    for (const rootTask of taskRoots) {
      const foundTask = rootTask.getTask(selectedId);
      if (foundTask) {
        taskToDuplicate = foundTask;
        break;
      }
    }

    if (!taskToDuplicate) {
      setDuplicateError('Selected task could not be found');
      return;
    }

    const duplicatedTask = taskToDuplicate.duplicate();
    setTask(duplicatedTask);
    setPendingChanges(false);
    closeDuplicateModal();
  };

  const renderDuplicateModal = () => {
    if (!showDuplicateModal) return null;

    return (
      <TaskSelectionDialog
        idPrefix="task-manager-duplicate"
        open={showDuplicateModal}
        onClose={closeDuplicateModal}
        onConfirm={duplicateSelectedTask}
        tasks={currentTasks}
        title="Select Task to Copy"
        confirmLabel="Duplicate"
        error={duplicateError}
        isLoading={isLoadingDuplicateTasks}
      />
    );
  };

  const renderAIModal = () => {
    if (!showAIModal) return null;

    const rawPriority = Task.calculatePriority(
      aiForm.importance,
      aiForm.dueDate || null,
      new Date()
    );
    const displayedPriority = Task.formatUserViewPriority(rawPriority);

    return (
      <Dialog id="task-manager-ai-dialog" open={showAIModal} onClose={() => setShowAIModal(false)}>
        <DialogTitle id="task-manager-ai-title">Generate Task with AI</DialogTitle>
        <DialogContent>
          {isGenerating ? (
            <Box sx={{ py: 4, minWidth: { xs: 260, sm: 420 }, display: 'flex', justifyContent: 'center' }}>
              <LoadingIndicator text="generating task" />
            </Box>
          ) : (
            <form id="task-manager-ai-form" onSubmit={handleAISubmit}>
              <Stack id="task-manager-ai-fields" spacing={3}>
                <TextField
                  id="task-manager-ai-description"
                  name="prompt"
                  label="Task Description"
                  multiline
                  rows={4}
                  fullWidth
                  value={aiForm.prompt}
                  onChange={(e) => handleAiFieldChange('prompt', e.target.value)}
                  placeholder="Describe your task in detail. For example: 'Create a task for planning a weekend trip to the beach, including packing, travel arrangements, and activities'"
                />

                <CategoryPicker
                  idPrefix="task-manager-ai-category"
                  label="Category"
                  value={aiForm.category_id}
                  onChange={(categoryId, category) => setAiForm((current) => ({
                    ...current,
                    category_id: categoryId,
                    category: category?.name || 'no-category',
                  }))}
                />

                <PriorityPanel
                  id="task-form-priority-panel"
                  priority={displayedPriority}
                  importanceControl={(
                    <FormControl id="task-manager-ai-importance" fullWidth>
                      <Typography component="label" sx={{ mb: 1 }}>
                        Importance
                      </Typography>
                      <EqBoosterSlider
                        value={aiForm.importance}
                        onChange={(value) => handleAiFieldChange('importance', value)}
                        min={1}
                        max={10}
                        increments={10}
                        minHeight={14}
                        maxHeight={48}
                        ariaLabel="Importance"
                      />
                    </FormControl>
                  )}
                  dueDateControl={(
                    <FormControl id="task-manager-ai-due-date" fullWidth>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Due Date"
                          value={aiForm.dueDate ? dayjs(aiForm.dueDate) : null}
                          onChange={(newValue) =>
                            handleAiFieldChange('dueDate', newValue ? newValue.format('YYYY-MM-DD') : '')
                          }
                          slotProps={{
                            textField: { fullWidth: true },
                            openPickerButton: { sx: { color: 'primary.main' } },
                          }}
                        />
                      </LocalizationProvider>
                    </FormControl>
                  )}
                />

                <FormControlLabel
                  control={
                    <Switch
                      id="task-manager-ai-recurring"
                      name="frequency"
                      checked={aiForm.frequency}
                      onChange={(e) => handleAiFieldChange('frequency', e.target.checked)}
                    />
                  }
                  label="Recurring Task"
                />

                <FormControlLabel
                  control={
                    <Switch
                      id="task-manager-ai-time-estimation"
                      name="timeEstimation"
                      checked={aiForm.timeEstimation}
                      onChange={(e) => handleAiFieldChange('timeEstimation', e.target.checked)}
                    />
                  }
                  label="Include Time Estimation"
                />

                <FormControl fullWidth>
                  <Typography gutterBottom>How many levels of subtasks</Typography>
                  <Slider
                    id="task-manager-ai-breakdowns"
                    name="breakdowns"
                    value={aiForm.breakdowns}
                    onChange={(_, value) => handleAiFieldChange('breakdowns', value)}
                    valueLabelDisplay="auto"
                    step={1}
                    marks
                    min={1}
                    max={5}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Typography gutterBottom>how overwhelming?</Typography>
                  <Slider
                    id="task-manager-ai-difficulty"
                    name="difficulty"
                    value={aiForm.difficulty}
                    onChange={(_, value) => handleAiFieldChange('difficulty', value)}
                    valueLabelDisplay="auto"
                    step={1}
                    marks
                    min={1}
                    max={10}
                  />
                </FormControl>

                <Stack
                  id="task-manager-ai-actions"
                  direction="row"
                  spacing={2}
                  justifyContent="flex-end"
                >
                  <Button
                    id="task-manager-ai-cancel"
                    type="button"
                    onClick={() => {
                      setShowAIModal(false);
                      setAiError(null);
                    }}
                    disabled={isGenerating}
                  >
                    Cancel
                  </Button>
                  <Button
                    id="task-manager-ai-generate"
                    type="submit"
                    variant="contained"
                    disabled={isGenerating}
                  >
                    Generate Task
                  </Button>
                </Stack>

                {aiError && (
                  <Alert severity="error">{aiError}</Alert>
                )}
              </Stack>
            </form>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div id="task-manager-page">
      <h2 id="task-manager-title">{isEditing ? 'Edit Task' : 'Create New Task'}</h2>
      
      {error && <div style={{ color: 'red', marginBottom: '15px' }}>{error}</div>}
      
      {pendingChanges && (
        <div>
          You have unsaved changes. Remember to click "Update Task" to save your changes.
        </div>
      )}
      
      {!loading && !task && !isEditing && (
        <Stack
          id="task-manager-start-options"
          direction="row"
          spacing={{ xs: 2, sm: 4 }}
          justifyContent="center"
          alignItems="flex-start"
          sx={{ mb: 2, width: '100%' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: { sm: 96 } }}>
            <Tooltip title="Add task manually">
              <IconButton id="task-manager-add-manual" aria-label="Add task manually" onClick={handleCreateManualTask}>
                <AddBox color="green" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, mt: 0.5 }}>
              Add task
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: { sm: 96 } }}>
            <Tooltip title="Duplicate a task">
              <IconButton id="task-manager-duplicate" aria-label="Duplicate a task" onClick={openDuplicateModal}>
                <ContentCopy color="orange" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, mt: 0.5 }}>
              Duplicate
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: { sm: 96 } }}>
            <Tooltip title="Generate task with AI">
              <IconButton id="task-manager-use-ai" aria-label="Generate task with AI" onClick={() => setShowAIModal(true)}>
                <AutoFixHigh color="primary" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, mt: 0.5 }}>
              Use AI
            </Typography>
          </Box>
        </Stack>
      )}
      
      {loading ? (
        <LoadingIndicator text="getting task" />
      ) : task ? (
        <div>
          <TaskForm 
            idPrefix="task-manager-editor"
            onSubmit={handleTaskSubmit} 
            initialTask={task} 
            onSubtaskDelete={handleSubtaskDelete}
            onRootDelete={handleRootDelete}
            submitButtonText={"Save Task"}
          />
        </div>
      ) : null}
       <Dialog id="task-manager-manual-dialog" open={!!manualTask} onClose={handleClose}>
  <DialogTitle>{isEditing ? 'Edit Task' : 'Add Task'}</DialogTitle>
  <DialogContent>
    <TaskInputForm
      idPrefix="task-manager-manual"
      task={manualTask}
      buttonText={isEditing ? 'Update' : 'Add'}
      buttonClick={handleTaskUpdate}
      handleClose={handleClose}
    />
  </DialogContent>
</Dialog>
      {renderAIModal()}
      <SubscriptionDialog
        open={showSubscriptionDialog}
        onClose={returnToAIForm}
        onAccessGranted={returnToAIForm}
        quota={quotaDetails}
      />
      {renderDuplicateModal()}
    </div>
  );
};

export default TaskManager;
